import { useTranslation } from 'react-i18next';
import { riskReasonKey, severityClassName, type IRiskScore, type ISafetyRisk, type RiskSeverity } from '../model/risk';
import type { IRelatedTasks } from '../model/listItem';
import { DependencyTooltip } from './DependencyTooltip';
import { ReasonsTooltip } from './ReasonsTooltip';
import styles from './RiskBadge.module.css';

const TONE_CLASS: Record<RiskSeverity, string | undefined> = {
  none: styles.none, low: styles.low, mid: styles.mid, high: styles.high,
};
const tone = (level: number): string => severityClassName(level, TONE_CLASS);

/**
 * The reason shown under a risk badge. Reasons are ranked most-impactful first,
 * so the highest one is the label; any others collapse into a "+N" suffix and
 * are revealed on hover (there isn't room to show them all on the card).
 */
function ReasonLabel({ reasons, relatedTasks, tz }: { reasons: string[]; relatedTasks?: IRelatedTasks; tz: string }) {
  const { t } = useTranslation();
  if (reasons.length === 0 || !reasons[0]) return null;

  const extra = reasons.length - 1;
  const text = t(riskReasonKey(reasons[0])) + (extra > 0 ? ` +${extra}` : '');
  const label = <span className={styles.reason}>{text}</span>;

  // The dependency reason keeps its richer tooltip (lists the linked tasks),
  // whether it's the headline reason or rides along as the "+1" on an overdue /
  // behind-schedule card. The reasons are passed through so the hover also names
  // every reason — otherwise a "+1" would expand to the task list without ever
  // saying what the extra reason was.
  if (
    reasons.includes('critical-path') &&
    relatedTasks &&
    (relatedTasks.predecessors.length > 0 || relatedTasks.successors.length > 0)
  ) {
    return (
      <DependencyTooltip
        reasons={reasons}
        predecessors={relatedTasks.predecessors}
        successors={relatedTasks.successors}
        tz={tz}
      >
        {label}
      </DependencyTooltip>
    );
  }
  // 2+ reasons → hover reveals the full set.
  if (extra > 0) {
    return <ReasonsTooltip reasons={reasons}>{label}</ReasonsTooltip>;
  }
  return label;
}

export interface IRiskBadgesProps {
  /** Project tz, forwarded to the dependency tooltip's year elision. */
  tz: string;
  onTime: IRiskScore;
  safety: ISafetyRisk;
  showReasons?: boolean;
  relatedTasks?: IRelatedTasks;
}

export function RiskBadges({ onTime, safety, showReasons, relatedTasks, tz }: IRiskBadgesProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.badges}>
      <div className={styles.badgeGroup}>
        <span className={`${styles.badge} ${tone(onTime.level)}`} title={onTime.reasons.join(', ')}>
          {t('activities.card.onTime', { n: onTime.level })}
        </span>
        {showReasons && onTime.level > 0 && (
          <ReasonLabel reasons={onTime.reasons} relatedTasks={relatedTasks} tz={tz} />
        )}
      </div>
      <div className={styles.badgeGroup}>
        <span className={`${styles.badge} ${tone(safety.level)}`} title={safety.reasons.join(', ')}>
          {t('activities.card.safety', { n: safety.level })}
        </span>
        {showReasons && safety.level > 0 && <ReasonLabel reasons={safety.reasons} tz={tz} />}
      </div>
    </div>
  );
}
